// BasicInfoSection.jsx
import React, { useEffect } from "react";
import ContactSection from "./ContactSection";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useTranslation } from "react-i18next";
import { useFlatpickrUserType } from "../../../hooks/useFlatpickrUserType";
const BasicInfoSection = ({
  form,
  setForm,
  errors,
  fileInputRef,
  dateInputRef,
  logoPreview,
  handleInputChange,
  clinicTypeOptions,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  
  // Show loading state while clinic data is being loaded
  if (isLoading) {
    return (
      <div className="clinicsetup-form">
        <h2 style={{ textAlign: "center" }}>{t("clinicSetup.basicInfo.title")}</h2>
        <p style={{ textAlign: "center" }}>
          {t("clinicSetup.basicInfo.loadingPrimary")}
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <p style={{ color: "#666", fontSize: "16px" }}>
            {t("clinicSetup.basicInfo.loadingSecondary")}
          </p>
        </div>
      </div>
    );
  }

  // Initialize Flatpickr for establishment date
  useEffect(() => {
    if (!dateInputRef.current || isLoading) {
      return;
    }

    // Clean up any existing flatpickr instance first
    if (dateInputRef.current._flatpickr) {
      try {
        dateInputRef.current._flatpickr.destroy();
      } catch (error) {
        console.error('Error destroying existing flatpickr instance:', error);
      }
    }

    // Clean up any leftover calendar containers in the DOM (but keep the one attached to our input)
    const existingCalendars = document.querySelectorAll('.flatpickr-calendar');
    existingCalendars.forEach(calendar => {
      // Only remove calendars that are not attached to any input
      const isAttachedToOurInput = calendar.parentElement && 
        (calendar.parentElement.contains(dateInputRef.current) || 
         dateInputRef.current.parentElement?.contains(calendar));
      if (!isAttachedToOurInput && calendar.parentNode) {
        calendar.parentNode.removeChild(calendar);
      }
    });

    let flatpickrInstance = null;
    
    try {
      flatpickrInstance = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d", // YYYY-MM-DD format to match backend expectations
        allowInput: true,
        clickOpens: true,
        maxDate: "today", // Prevent future dates for establishment date
        appendTo: document.body, // Append to body to avoid positioning issues
        altInput: false, // Disable alt input to prevent duplicate inputs
        onChange: function(selectedDates, dateStr, instance) {
          // Update form state with the selected date
          handleInputChange({
            target: {
              name: 'establishmentDate',
              value: dateStr
            }
          });
        },
        // Add some styling
        onReady: function(selectedDates, dateStr, instance) {
          if (instance.calendarContainer) {
            instance.calendarContainer.style.fontFamily = '"Red Hat Display", sans-serif';
          }
        }
      });
    } catch (error) {
      console.error('Error initializing flatpickr:', error);
    }

    return () => {
      // Clean up the flatpickr instance
      if (flatpickrInstance && typeof flatpickrInstance.destroy === 'function') {
        try {
          flatpickrInstance.destroy();
        } catch (error) {
          console.error('Error destroying flatpickr instance:', error);
        }
      }
      // Also clean up any leftover calendar containers
      if (dateInputRef.current && dateInputRef.current._flatpickr) {
        try {
          dateInputRef.current._flatpickr.destroy();
        } catch (error) {
          // Ignore errors during cleanup
        }
      }
    };
  }, [isLoading]); // Only depend on isLoading to prevent re-initialization on handleInputChange

  // Additional cleanup effect to remove duplicate inputs (but keep the main one)
  useEffect(() => {
    if (isLoading || !dateInputRef.current) return;

    const cleanupDuplicates = () => {
      if (!dateInputRef.current) return;
      
      // Store reference to the main input
      const mainInput = dateInputRef.current;
      
      // Find all inputs with the same name in the parent, but keep the main one
      const parentElement = mainInput.parentElement;
      if (parentElement) {
        const allInputs = Array.from(parentElement.querySelectorAll('input[type="text"]'));
        allInputs.forEach(input => {
          // Keep only the main input (the one with our ref and data attribute), hide/remove any duplicates
          if (input !== mainInput && 
              !input.hasAttribute('data-flatpickr-main') &&
              (input.name === 'establishmentDate' || 
               input.classList.contains('flatpickr-alt-input'))) {
            // Hide and remove duplicates
            input.style.display = 'none';
            input.style.visibility = 'hidden';
            input.remove();
          }
        });
      }
    };

    // Run cleanup after a delay to ensure flatpickr has initialized
    const timeoutId = setTimeout(cleanupDuplicates, 300);
    const timeoutId2 = setTimeout(cleanupDuplicates, 800);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, [isLoading]);

  return (
    <div
      style={{
        padding: "0",
        width: "100%",
        maxWidth: "960px",
        margin: "0 auto",
      }}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      
      {/* Logo Upload Section */}
      <div style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: "40px",
        marginTop: "40px"
      }}>
        <div
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          style={{
            width: "160px",
            height: "160px",
            borderRadius: "16px",
            border: logoPreview ? "2px solid #e5e7eb" : "2px dashed #d1d5db",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            cursor: "pointer",
            transition: "all 0.2s ease",
            background: logoPreview ? "#fff" : "#f9fafb",
            position: "relative",
            overflow: "hidden"
          }}
          onMouseEnter={(e) => {
            if (logoPreview) {
              e.currentTarget.style.borderColor = "#3b82f6";
            }
          }}
          onMouseLeave={(e) => {
            if (logoPreview) {
              e.currentTarget.style.borderColor = "#e5e7eb";
            }
          }}
        >
          {logoPreview ? (
            <>
              <img
                src={logoPreview}
                alt={t("clinicSetup.basicInfo.logo.previewAlt")}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "14px"
                }}
              />
              <div style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.2s ease",
                borderRadius: "14px"
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
              >
                <span style={{ color: "#fff", fontSize: "14px", fontWeight: "500" }}>
                  {t("clinicSetup.basicInfo.logo.change")}
                </span>
              </div>
            </>
          ) : (
            <>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <span style={{ fontSize: "14px", color: "#6b7280", marginTop: "12px", fontWeight: "500" }}>
                {t("clinicSetup.basicInfo.logo.upload")}
              </span>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          name="logo"
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
      </div>

      {/* Form Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {/* Name Fields Row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
          <div>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px"
            }}>
              {t("clinicSetup.basicInfo.fields.nameEn.label")} <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              name="name_en"
              value={form.name_en}
              onChange={handleInputChange}
              placeholder={t("clinicSetup.basicInfo.fields.nameEn.placeholder")}
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "15px",
                border: errors.name_en ? "2px solid #ef4444" : "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                transition: "all 0.2s ease",
                background: "#fff",
                boxSizing: "border-box"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.name_en ? "#ef4444" : "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
            {errors.name_en && (
              <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                {errors.name_en}
              </div>
            )}
          </div>
          <div>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px"
            }}>
              {t("clinicSetup.basicInfo.fields.nameAr.label")} <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              onKeyUp={(e) => {
                if (e.target.value.length > 0) {
                  e.target.dir = "rtl";
                } else {
                  e.target.dir = "ltr";
                }
              }}
              type="text"
              name="name_ar"
              value={form.name_ar}
              onChange={handleInputChange}
              placeholder={t("clinicSetup.basicInfo.fields.nameAr.placeholder")}
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "15px",
                border: errors.name_ar ? "2px solid #ef4444" : "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                transition: "all 0.2s ease",
                background: "#fff",
                boxSizing: "border-box"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.name_ar ? "#ef4444" : "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
            {errors.name_ar && (
              <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                {errors.name_ar}
              </div>
            )}
          </div>
        </div>

        {/* Date and Type Row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
          <div style={{ position: "relative" }}>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px"
            }}>
              {t("clinicSetup.basicInfo.fields.establishmentDate.label")}
            </label>
            <input
              ref={dateInputRef}
              type="text"
              name="establishmentDate"
              value={form.establishmentDate || ''}
              onChange={handleInputChange}
              placeholder={t("clinicSetup.basicInfo.fields.establishmentDate.placeholder")}
              readOnly
              data-flatpickr-main="true"
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "15px",
                border: "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                cursor: "pointer",
                background: "#fff",
                boxSizing: "border-box",
                transition: "all 0.2s ease"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
            {/* Hide any duplicate inputs that flatpickr might create, but keep the main one */}
            <style>{`
              input[name="establishmentDate"]:not([data-flatpickr-main="true"]),
              input[name="establishmentDate"] ~ input[type="text"]:not([data-flatpickr-main="true"]),
              .flatpickr-alt-input,
              .flatpickr-input:not([data-flatpickr-main="true"]) {
                display: none !important;
                visibility: hidden !important;
              }
            `}</style>
          </div>
          <div>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px"
            }}>
              {t("clinicSetup.basicInfo.fields.clinicType.label")} <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              name="type"
              value={form.type}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "12px 40px 12px 16px",
                fontSize: "15px",
                border: errors.type ? "2px solid #ef4444" : "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                cursor: "pointer",
                background: "#fff",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 16px center",
                boxSizing: "border-box",
                transition: "all 0.2s ease",
                appearance: "none"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.type ? "#ef4444" : "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            >
              <option value="" disabled>
                {t("clinicSetup.basicInfo.fields.clinicType.placeholder")}
              </option>
              {clinicTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.type && (
              <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                {errors.type}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div style={{ marginTop: "32px" }}>
        <ContactSection
          form={form}
          errors={errors}
          handleInputChange={handleInputChange}
        />
      </div>
    </div>
  );
};

export default BasicInfoSection;
